import RichPlaceholderCompiler from '../compilers/RichPlaceholderCompiler'
import BlueprintUtils from '../blueprintUtils.js'
import { WIDGET_PREVIEW_SIZE } from '../constants'

function parseElementStyle(props) {
  const { widget, isHyperlinkEnabled, zModifier, isRenderingInsideLayerTool, isRenderingInsideAceEditor } = props
  let widgetLayout = widget.layout || {}
  let widgetBaseStyle = widget.baseStyle ? JSON.parse(JSON.stringify(widget.baseStyle)) : {}
  let animationTypeMap = {}
  // In preparation for multiple animations, animation fill mode is converted to 'forwards' instead of 'both'.
  // This is because the 'both' fill mode does not work with multiple animations. This then requires us to set
  // the position of the actual widget to the starting point of the animation otherwise the widget will become visible
  // in the end position of the animation and not the starting position.
  if (widget.animations && !isRenderingInsideAceEditor) {
    widget.animations.forEach(a => {
      if (
        (a.type !== 'transition' && animationTypeMap[a.type]) ||
        (a.type === 'transition' && animationTypeMap[a.targetStyle.name])
      ) {
        return
      }

      switch (true) {
        case a.type === 'move' || a.type === 'resize':
          animationTypeMap['move'] = true
          animationTypeMap['resize'] = true
          widgetLayout = {
            ...widgetLayout,
            x: a.animationStates[0].layout.x,
            y: a.animationStates[0].layout.y,
            width: a.animationStates[0].layout.width,
            height: a.animationStates[0].layout.height,
          }
          break
        case a.type === 'transition':
          animationTypeMap[a.targetStyle.name] = true
          widgetBaseStyle[a.targetStyle.name] = a.animationStates[0].transition.value
          break
        case a.type === 'rotation':
          animationTypeMap[a.type] = true
          widgetLayout = {
            ...widgetLayout,
            zRotation: a.animationStates[0].rotation.value,
          }
          widgetBaseStyle.horizontal = a.animationStates[0].horizontal.value
          widgetBaseStyle.vertical = a.animationStates[0].vertical.value
          break
      }
    })
  }
  // if inside ace editor, set to current base style, not starting point of animation
  if (isRenderingInsideAceEditor) {
    widgetBaseStyle = widget.baseStyle
  }
  const scale =
    isRenderingInsideLayerTool && WIDGET_PREVIEW_SIZE / Math.max(widgetLayout.width, widgetLayout.height) < 1
      ? `scale(${WIDGET_PREVIEW_SIZE / Math.max(widgetLayout.width, widgetLayout.height)})`
      : ''

  // extraHeight is used to add extra space to the height of the widget
  const extraHeight = widgetLayout?.extraHeight || 0
  // check 'isSquareImage' if true, then set the width and height to the same value
  // this is to make sure the image is always square
  // as totals height including imageHeight + extraHeight, need to subtract extraHeight to check if the image width and height are equal
  if (widgetLayout?.isSquareImage && widgetLayout.width !== widgetLayout.height - extraHeight) {
    const minNum = Math.min(widgetLayout.width, widgetLayout.height)
    widgetLayout.width = minNum - extraHeight
    widgetLayout.height = minNum
  }

  return {
    position: 'absolute',
    overflow: 'hidden',

    // Allow link-less widgets to be non-interactive, so that user could
    // interact with underlying links
    'pointer-events': isHyperlinkEnabled && !widget.href && 'none',

    width: `${widgetLayout.width}px`,
    height: `${widgetLayout.height}px`,
    top: isRenderingInsideLayerTool ? '' : `${widgetLayout.y}px`,
    left: isRenderingInsideLayerTool ? '' : `${widgetLayout.x}px`,
    'z-index': widgetLayout.z + zModifier,
    transform: isRenderingInsideLayerTool ? `${scale}` : `rotate(${widgetLayout.zRotation || 0}deg)`,
  }
}

// convert rp square brackets to real value
// e.g. [["displayText":"Display a side for x seconds","value":"2.0","type":"float"]] --> 2.0
function compileRPSquareBrackets(codesnippetHTML) {
  const rpSquareBrackets = BlueprintUtils.get.RPsSquareBracketFromHtmlTemplate(codesnippetHTML)

  rpSquareBrackets.forEach(rp => {
    // change rp object to square bracket format
    const rpSquareBracket = `[[${JSON.stringify(rp).replace(/{|}/g, '')}]]`
    // replace rp square bracket with value
    codesnippetHTML = codesnippetHTML.replace(rpSquareBracket, rp.value)
  })

  return codesnippetHTML
}

function withDivContainer($h) {
  //context was bound to this
  const props = this.props
  const { widget, isRenderingInsideAceEditor } = props
  //account for any native events the template editor is trying to apply (ie mouseover, contextmenu)
  const nativeOn = (this.data && this.data.nativeOn) || {}

  const codeSnippest = widget.codesnippet
  const codesnippetHTML = isRenderingInsideAceEditor
    ? codeSnippest.previewHTML
    : compileRPSquareBrackets(codeSnippest.codesnippetHTML + codeSnippest.codesnippetCSS + codeSnippest.codesnippetJS)

  return $h(
    'div',
    {
      on: {
        ...nativeOn,
      },
      style: parseElementStyle(props),
      attrs: {
        'data-adacadotransitiontype': 'transition',
        'widget-id': `widget${props.widget.id}`,
        class: props.animationClass,
      },
      domProps: {
        innerHTML: codesnippetHTML,
      },
    },
    []
  )
}

function withAnchorContainer(sourceElement, $h) {
  // Wrap widget in an anchor tag if there is a value for href property
  if (this.isHyperlinkEnabled && this.widget.href) {
    return $h(
      'a',
      {
        attrs: {
          // href attributes of widget are completely controlled by the end-user,
          // so it is important to sanitize the inputs in all cases.
          // FIXME: we are performing bare minimum checks that replaces `<` and `>`
          // Due to lack of support from vue(see  https://github.com/vuejs/vue/issues/8657),
          // we would have to incorporate other libraries for a complete sanitization logic
          href: escapeLinkAttribute(this.contentCompiler.compile(this.widget.href, { isURL: true })),
          target: '_blank',
        },
      },
      [sourceElement]
    )
  } else {
    // pointer-events attribute will be set to "none" when isHyperlinkEnabled is true but widget.href is not defined
    // this allows the widget to inherit link behavior from the background
    return sourceElement
  }
}

function escapeLinkAttribute(rawString) {
  return rawString.replace(/[<>]/g, substr => {
    return (
      {
        '<': '&lt;',
        '>': '&gt;',
      }[substr] || substr
    )
  })
}

export default {
  functional: true,
  props: {
    widget: {
      type: Object,
      required: true,
    },
    animationClass: {
      type: String,
      default: '',
    },
    templateId: Number,
    isHyperlinkEnabled: Boolean,
    zModifier: {
      type: Number,
      default: 0,
    },
    inventoryIndex: {
      type: Number,
      default: 0,
    },
    // CanvasTranspiler will pass this a prop where the RichPlaceholderCompiler's mode is set to 'placeholder'
    // which generates richplaceholders in the output html as opposed to just the default value (for viewing in Ace Editor)
    contentCompiler: {
      type: Object,
      default() {
        return new RichPlaceholderCompiler()
      },
    },
    isRenderingInsideAceEditor: {
      type: Boolean,
      default: false,
    },
    isRenderingInsideLayerTool: {
      type: Boolean,
      default: false,
    },
  },
  render($h, context) {
    // decorate with div, and then finally anchor
    return withAnchorContainer.call(context.props, withDivContainer.call(context, $h), $h)
  },
}
